<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { selectProps, type SelectOption } from 'naive-ui';
import GaiaRemoteSelect, { type GaiaRemotePage } from '../../base/selects/GaiaRemoteSelect.vue';
import { PermissionsService, isMemberRole } from '@/services/permissionsService';
import type { PermissionRoleInDb } from '@/api';

/** Picks one or more permission roles, by uid. */
const props = defineProps({
  ...selectProps,
  /** Roles per request. The search endpoint caps this at 50. */
  pageSize: {
    type: Number,
    default: 50,
  },
  /**
   * Drop the built-in "Organisation Member" role from the list. Every user
   * gets it on accept, so offering it is noise.
   */
  excludeProtectedMember: {
    type: Boolean,
    default: false,
  },
  /**
   * Roles to keep pickable even when the search does not return them — the
   * roles already linked to a record being edited, which arrive as uids the
   * search has not seen yet. Same idea as GaiaUserSelect's `seedUsers`.
   */
  seedRoles: {
    type: Array as PropType<PermissionRoleInDb[]>,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Search roles',
  },
});

function toOption(role: PermissionRoleInDb): SelectOption {
  return { label: role.name, value: role.uid };
}

const seedOptions = computed<SelectOption[]>(() => props.seedRoles.map(toOption));

function isExcluded(role: PermissionRoleInDb): boolean {
  return props.excludeProtectedMember && isMemberRole(role);
}

async function fetchRoles(query: string, page: number): Promise<GaiaRemotePage> {
  const { results, result_count } = await PermissionsService.searchRoles({
    text: query,
    page,
    pageSize: props.pageSize,
    sortKey: 'name',
    sortDirection: 'asc',
  });
  const options: SelectOption[] = results.filter(role => !isExcluded(role)).map(toOption);
  // Paging is judged on what the server returned, not on what survived the
  // filter — dropping one row must not read as "that was the last page".
  return { options, hasMore: page * props.pageSize < result_count };
}

const selectBindings = computed<Record<string, unknown>>(() => {
  const bindings: Record<string, unknown> = { ...props };
  delete bindings.pageSize;
  delete bindings.excludeProtectedMember;
  delete bindings.seedRoles;
  return bindings;
});
</script>

<template>
  <gaia-remote-select v-bind="selectBindings" :fetch="fetchRoles" :seed-options="seedOptions" />
</template>
